<template>
    <div>
        <form v-on:submit.prevent="onSubmit()">
            <div v-if="errors" v-html="errors" class="alert alert-danger" role="alert"></div>

            <div class="row">
                <div class="col-lg-4 col-md-6">
                    <div class="form-group">
                        <label
                            :for="'template-font-weight' + template_font_file.id"
                            :id="'template-font-weight-label' + template_font_file.id"
                            class="mandatory-label"
                        >
                            {{ $root.text('Weight', null, true) }} *
                        </label>
                        <searchable-option-list
                            :key="'weight-' + ($root.current_language || 0)"
                            v-model="template_font_file.weight"
                            :value="template_font_file.weight"
                            :options="getWeightOptions()"
                            :field_id="'template-font-weight' + template_font_file.id"
                            field_class="mandatory-field"
                            :readonly="updating"
                            :search_placeholder="$root.text('Select font weight', null, true)"
                            v-on:change="$root.unHighlightErrors('template-font-weight', template_font_file.id)"
                        ></searchable-option-list>
                    </div>
                    <div class="form-group">
                        <label
                            :for="'template-font-style' + template_font_file.id"
                            :id="'template-font-style-label' + template_font_file.id"
                            class="mandatory-label"
                        >
                            {{ $root.text('Style', null, true) }} *
                        </label>
                        <searchable-option-list
                            :key="'style-' + ($root.current_language || 0)"
                            v-model="template_font_file.style"
                            :value="template_font_file.style"
                            :options="getStyleOptions()"
                            :field_id="'template-font-style' + template_font_file.id"
                            field_class="mandatory-field"
                            :readonly="updating"
                            :search_placeholder="$root.text('Select font style', null, true)"
                            v-on:change="$root.unHighlightErrors('template-font-style', template_font_file.id)"
                        ></searchable-option-list>
                    </div>
                </div>
                <div class="col-lg-4 col-md-6">
                    <div class="form-group">
                        <label>{{ $root.text('Replace file', null, true) }}</label>
                        <vue-dropzone
                            v-if="dropzone_options"
                            ref="templateFontFileDropzone"
                            :id="'template-font-file-dropzone' + template_font_file.id"
                            :options="dropzone_options"
                            v-on:vdropzone-sending="onSendingFile"
                            v-on:vdropzone-upload-progress="onProgressUpdate"
                            v-on:vdropzone-success="onFileUploaded"
                            v-on:vdropzone-error="onUploadError"
                            v-on:vdropzone-drag-over="onDragOver"
                            v-on:vdropzone-drag-leave="onDragLeave"
                            v-on:vdropzone-drop="onDragLeave"
                            v-on:vdropzone-file-added="onFileAdded"
                            :useCustomSlot=true
                        >
                            <div v-if="file" class="dz-uploaded-file p-2 mb-1">
                                <div class="dz-thumbnail">
                                    <span v-if="!file.preview_image">
                                        <img
                                            :src="$root.imagePlaceholder(file.filename)"
                                            class="img-fluid"
                                            alt=""
                                        >
                                    </span>
                                    <img
                                        v-else
                                        :src="file.preview_image"
                                        class="img-fluid"
                                        alt=""
                                    >
                                </div>
                                <div class="dz-filename">{{ file.filename }}</div>
                                <div class="dz-size" v-html="$root.formatFileSize(file.file_size)"></div>
                                <div class="dz-remove">
                                    <a
                                        v-if="!updating"
                                        href="#"
                                        v-b-tooltip.hover
                                        :title="$root.text('Delete', null, true)"
                                        @click.prevent="removeUploadedFile()"
                                    >
                                        <i class="fal fa-trash-alt"></i>
                                    </a>
                                    <span v-if="updating"><i class="fal fa-trash-alt"></i></span>
                                </div>
                                <div
                                    class="dz-upload-info"
                                    :id="'upload-info' + file.id"
                                    style="left: 160px"
                                >
                                    <!--<i class="fal fa-clock mr-1"></i> Pending upload-->
                                </div>
                                <div
                                    class="dz-progress"
                                    :id="'upload-progress' + file.id"
                                    style="width: calc(100% - 172px)"
                                >
                                    <div class="dz-upload"></div>
                                </div>
                            </div>
                            <a
                                href="#"
                                class="dz-add-files"
                                :id="'template-font-file-add-files' + template_font_file.id"
                                @click.prevent="doNothing()"
                                :style="dzAddFilesStyle()"
                            >
                                <i class="far fa-cloud-upload-alt font-size-20 mr-1"></i>
                                {{ $root.text('Click or drop font file here to upload.', null, true) }}
                                <br>
                                {{ $root.text('Supported types: TTF, OTF, WOFF or WOFF2', null, true) }}
                            </a>
                        </vue-dropzone>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col">
                    <span v-if="!updating">
                        <button
                            :key="'submit-button'"
                            type="submit"
                            class="btn btn-primary mr-2"
                            :disabled="uploading"
                        >
                            <i class="far fa-save mr-1"></i> {{ $root.text('Save', null, true) }}
                        </button>
                    </span>
                    <span v-else>
                        <button :key="'submitting-button'" type="button" class="btn btn-primary mr-2">
                            <i class="fal fa-spinner-third fa-spin mr-1"></i>
                            <span style="opacity: 0.5">{{ $root.text('Save', null, true) }}</span>
                        </button>
                    </span>
                    <button
                        :key="'delete-button'"
                        type="button"
                        class="btn btn-secondary"
                        :disabled="updating || uploading"
                        @click.prevent="deleteTemplateFontFile()"
                    >
                        <i class="far fa-trash-alt mr-1"></i> {{ $root.text('Delete', null, true) }}
                    </button>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
    import vue2Dropzone from 'vue2-dropzone';
    export default {
        props: ['data', 'is_visible'],
        components: {
            'vue-dropzone': vue2Dropzone
        },
        data () {
            return {
                errors: false,
                uploading: false,
                dropzone_options: null,
                file: null,
                updating: false,
                template_font_file: {
                    id: 0,
                }
            }
        },
        mounted () {
            if (this.data) {
                this.template_font_file = this.data;
            }
        },
        watch: {
            data: function (val) {
                this.template_font_file = val;
            },
            is_visible: function (val) {
                if (!val || (this.dropzone_options !== null)) {
                    return null;
                }
                this.initDropzone();
            }
        },
        methods: {
            initDropzone: function () {
                var vm = this;
                this.dropzone_options = {
                    url: this.$root.app.api_url + '/' + this.$root.app.client_id + '/upload-v2',
                    acceptedFiles: '.ttf,.otf,.woff,.woff2',
                    createImageThumbnails: true,
                    maxThumbnailFilesize: 20,
                    thumbnailWidth: 50,
                    thumbnailHeight: 50,
                    uploadMultiple: false,
                    parallelUploads: 1,
                    chunking: true,
                    forceChunking: true,
                    parallelChunkUploads: false,
                    chunkSize: 10000000,
                    retryChunks: true,
                    retryChunksLimit: 5,
                    maxFilesize: (10 * 1000 * 1000),
                    autoProcessQueue: false,
                    timeout: 0,
                    clickable: '#template-font-file-add-files' + this.template_font_file.id,
                    headers: {
                        'Authorization': 'Bearer ' + this.$root.token
                    },
                    accept: function (file, done) {
                        const ext = vm.$root.getFileExtension(file.name);
                        const allowedExtensions = ['ttf', 'otf', 'woff', 'woff2'];
                        if (allowedExtensions.indexOf(ext) < 0) {
                            done('File type not accepted');

                            return null;
                        }
                        done();
                    }
                };
            },
            onSubmit: function () {
                this.errors = false;
                this.updating = true;
                var vm = this;
                $('.mandatory-label').removeClass('text-danger');
                $('.mandatory-field').removeClass('field-error');

                /**
                 * Send update template font file request to API.
                 */
                let data = this.template_font_file;
                if (this.file) {
                    data.temp_file = this.file.temp_file;
                    data.filename = this.file.filename;
                    data.file_type = this.file.file_type;
                    data.file_size = this.file.file_size;
                }
                const url = this.$root.app.api_url + '/' + this.$root.app.client_id
                    + '/template-font-file/' + this.template_font_file.id;

                axios({
                    method: 'put',
                    url: url,
                    data: data,
                    headers: { 'Authorization': 'Bearer ' + vm.$root.token }
                })
                .then(response => {
                    if (vm.file) {
                        // Update font files.
                        vm.$root.updateTemplateFonts();
                    }
                    // Show confirmation message.
                    const appComponent = vm.$root.findComponent(vm.$root, 'main-app');
                    appComponent.message = {
                        type: 'success',
                        title: vm.$root.text(
                            'Update {template} font file',
                            {
                                template: vm.$root.text(
                                    vm.$root.contents.template_title,
                                    null,
                                    false,
                                    'template_title'
                                )
                            },
                            true
                        ),
                        text: response.data.message
                    };
                    $('#message-modal').modal('show');

                    // Close after 2 seconds automatically.
                    setTimeout(function() {
                        $('#message-modal').modal('hide');
                    }, 2000);
                })
                .catch(function (error) {
                    if (error.response.status == 401) {
                        // Missing/lost API token - logout user.
                        vm.$root.doLogout();
                    }
                    if (error.response.data.error) {
                        vm.errors = vm.$root.text('Something went wrong, please try again', null, true);
                        if (typeof error.response.data.error === 'string') {
                            vm.errors = error.response.data.error;
                        } else if (typeof error.response.data.error === 'object') {
                            vm.errors = '';
                            for (const [key, value] of Object.entries(error.response.data.error)) {
                                vm.errors += value.join('<br>') + '<br>';
                                $('#template-font-' + key + '-label' + vm.template_font_file.id).addClass('text-danger');
                                $('#template-font-' + key + vm.template_font_file.id).addClass('field-error');
                            }
                        }
                    }
                })
                .finally(() => {
                    vm.updating = false;
                });
            },
            deleteTemplateFontFile: function (confirmed = false) {
                if (confirmed) {
                    this.errors = false;
                    this.$parent.$parent.files_loading = true;

                    /**
                     * Send delete template font file request to API.
                     */
                    const url = this.$root.app.api_url + '/' + this.$root.app.client_id
                        + '/template-font-file/' + this.template_font_file.id;
                    var vm = this;

                    axios({
                        method: 'delete',
                        url: url,
                        headers: { 'Authorization': 'Bearer ' + vm.$root.token }
                    })
                    .then(response => {
                        vm.$parent.$parent.getTemplateFontFiles();

                        // Update font files.
                        vm.$root.updateTemplateFonts();
                    })
                    .catch(function (error) {
                        if (error.response.status == 401) {
                            // Missing/lost API token - logout user.
                            vm.$root.doLogout();
                        }
                        if (error.response.data.error) {
                            vm.errors = error.response.data.error;
                        }
                    });

                    return null;
                }

                if (this.updating) {
                    return null;
                }

                const appComponent = this.$root.findComponent(this.$root, 'main-app');
                appComponent.confirm_delete = {
                    title: this.$root.text(
                        'Delete {template} font file',
                        {
                            template: this.$root.text(
                                this.$root.contents.template_title,
                                null,
                                false,
                                'template_title'
                            )
                        },
                        true
                    ),
                    text: this.$root.text(
                        'Are you sure you want to delete: {name}?',
                        { name: this.template_font_file.filename },
                        true
                    ),
                    component: this,
                    action: 'delete-template-font-file'
                };
                $('#confirm-delete-modal').modal('show');
            },
            getWeightOptions: function () {
                const options = [
                    {
                        id: 'thin',
                        name: this.$root.text('thin', null, true)
                    },
                    {
                        id: 'extraLight',
                        name: this.$root.text('Extra light', null, true)
                    },
                    {
                        id: 'light',
                        name: this.$root.text('Light', null, true)
                    },
                    {
                        id: 'regular',
                        name: this.$root.text('Regular', null, true)
                    },
                    {
                        id: 'medium',
                        name: this.$root.text('Medium', null, true)
                    },
                    {
                        id: 'semiBold',
                        name: this.$root.text('Semi bold', null, true)
                    },
                    {
                        id: 'bold',
                        name: this.$root.text('Bold', null, true)
                    },
                    {
                        id: 'extraBold',
                        name: this.$root.text('Extra bold', null, true)
                    },
                    {
                        id: 'black',
                        name: this.$root.text('Black', null, true)
                    }
                ];

                return options;
            },
            getStyleOptions: function () {
                const options = [
                    {
                        id: 'normal',
                        name: this.$root.text('Normal', null, true)
                    },
                    {
                        id: 'italic',
                        name: this.$root.text('Italic', null, true)
                    }
                ];

                return options;
            },
            doNothing: function () {
                return false;
            },
            dzAddFilesStyle: function () {
                if (!this.file) {
                    return 'display: block';
                }

                return 'display: none';
            },
            onFileAdded: function (file) {
                if (!this.uploading) {
                    this.errors = false;
                }
                if (this.file) {
                    this.$refs.templateFontFileDropzone.removeFile(file);

                    return null;
                }

                // Check file size.
                if (file.size > this.dropzone_options.maxFilesize) {
                    this.$refs.templateFontFileDropzone.removeFile(file);
                    const appComponent = this.$root.findComponent(this.$root, 'main-app');
                    appComponent.message = {
                        type: 'error',
                        title: this.$root.text('Maximum upload size exceeded', null, true),
                        text: this.$root.text('The maximum size of the file you can upload is {size}.',
                            { size: this.$root.formatFileSize(this.dropzone_options.maxFilesize) }, true) + ' '
                            + this.$root.text('The size of {filename} is {size}.',
                            { filename: file.name, size: this.$root.formatFileSize(file.size) }, true)
                    };
                    $('#message-modal').modal('show');

                    return null;
                }

                this.errors = false;
                this.uploading = true;
                let newFile = {
                    id: file.upload.uuid,
                    filename: file.name,
                    file_type: file.type,
                    file_size: file.size,
                    upload: {
                        progress: 0,
                        bytes_sent: 0
                    },
                    preview_image: (file.dataURL || null)
                };
                this.file = newFile;
                var vm = this;
                setTimeout(function () {
                    if (vm.file) {
                        vm.$refs.templateFontFileDropzone.processQueue();
                        $('#upload-info' + vm.file.id).html(vm.$root.text('Preparing upload...', null, true));
                    }
                }, 500);
            },
            onDragOver: function (event) {
                $('#template-font-file-dropzone').addClass('dz-drag-highlight');
            },
            onDragLeave: function (event) {
                $('#template-font-file-dropzone').removeClass('dz-drag-highlight');
            },
            onSendingFile: function (file, xhr, formData) {
                formData.append('id', file.upload.uuid);
            },
            onProgressUpdate: function (file, progress, bytesSent) {
                if (!this.file) {
                    return null;
                }
                if ((progress < 100) && (this.file.id == file.upload.uuid)) {
                    if (isNaN(bytesSent)) {
                        bytesSent = file.size * (progress / 100);
                    }
                    this.file.upload.progress = progress;
                    this.file.upload.bytes_sent = bytesSent;
                    $('#upload-info' + this.file.id)
                        .html(this.$root.formatFileSize(bytesSent) + ' ' + progress.toFixed(1) + '%');
                    $('#upload-progress' + this.file.id + ' > div').css('width', progress + '%');
                }
            },
            onFileUploaded: function (file, response) {
                if (!this.file) {
                    return null;
                }
                if (this.file.id == file.upload.uuid) {
                    this.file.upload.progress = 100;
                    this.file.upload.bytes_sent = file.size;
                    $('#upload-info' + this.file.id).html(this.$root.text('Finalising upload...', null, true));
                    $('#upload-progress' + this.file.id + ' > div').css('width', '100%');
                    if (!this.file.preview_image) {
                        this.file.preview_image = (file.dataURL || null);
                    }

                    this.getUploadInfo(this.file.id);
                    this.$refs.templateFontFileDropzone.removeFile(file);
                }
            },
            getUploadInfo: function (fileId) {
                /**
                 * Send request to API.
                 */
                const url = this.$root.app.api_url + '/' + this.$root.app.client_id + '/upload-info/' + fileId;
                var vm = this;

                axios({
                    url: url,
                    headers: { 'Authorization': 'Bearer ' + vm.$root.token }
                })
                .then(response => {
                    if (vm.file.id != response.data.file_id) {
                        return null;
                    }
                    vm.file.temp_file = response.data.temp_file;
                    vm.onUploadComplete();
                    $('#upload-info' + vm.file.id).html('');
                })
                .catch(function (error) {
                    // Do anything?
                });
            },
            onUploadComplete: function () {
                this.uploading = false;
            },
            onUploadError: function (file, message, xhr) {
                if (file.status != 'canceled') {
                    if (!file.accepted || (message == 'File type not accepted')) {
                        let error = this.$root.text('Invalid file type: {filename}', { filename: file.name }, true);
                        if (!this.errors) {
                            error += '<br><br>'
                                + this.$root.text('Please choose a TTF, OTF, WOFF or WOFF2 file.', null, true);
                            this.errors = error;
                        } else {
                            this.errors = error + '<br>' + this.errors;
                        }
                        this.$refs.templateFontFileDropzone.removeFile(file);
                        this.file = null;
                    }
                    if (!this.errors) {
                        this.errors = this.$root.text('Something went wrong, please try again', null, true);
                    }
                }
                this.uploading = false;
                console.log(file, message);
            },
            removeUploadedFile: function(confirmed = false) {
                if (confirmed) {
                    const removeUploadFileId = (this.file.upload.progress < 100) ? this.file.id : null;

                    this.file = null;
                    this.errors = false;

                    if (removeUploadFileId) {
                        this.$refs.templateFontFileDropzone.getUploadingFiles().forEach((file) => {
                            if (file.upload.uuid == removeUploadFileId) {
                                this.$refs.templateFontFileDropzone.removeFile(file);
                            }
                        });
                        this.$refs.templateFontFileDropzone.getQueuedFiles().forEach((file) => {
                            if (file.upload.uuid == removeUploadFileId) {
                                this.$refs.templateFontFileDropzone.removeFile(file);
                            }
                        });
                    }
                
                    return null;
                }

                const appComponent = this.$root.findComponent(this.$root, 'main-app');
                appComponent.confirm_delete = {
                    title: this.$root.text('Delete file', null, true),
                    text: this.$root.text(
                        'Are you sure you want to delete: {filename}?',
                        { filename: this.file.filename },
                        true
                    ),
                    component: this,
                    action: 'delete-file'
                };
                $('#confirm-delete-modal').modal('show');
            },
            confirmDelete: function (action) {
                if (action == 'delete-file') {
                    this.removeUploadedFile(true);
                }
                if (action == 'delete-template-font-file') {
                    this.deleteTemplateFontFile(true);
                }
            },
        }
    }
</script>
<style scoped>
    .dropzone {
        min-height: 126px;
    }

    .dz-add-files {
        padding-top: 34px;
    }

    .dz-uploaded-file {
        cursor: default;
    }
</style>
